import { useEffect, useMemo, useState } from "react";
import { Domain } from "../../api/partner/models/Domain";
import { DNSRecord, DNSRecordType } from "../../api/partner/models/DNSZone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RecordTypeSelect } from "./RecordTypeSelect";
import { RecordNameInput } from "./RecordNameInput";
import { Button, IconButton } from "../form/Button";
import { RecordContentInputs } from "./RecordContentInputs";
import { v4 as uuid } from "uuid";
import { APIResponse } from "../../api/ModelFactory";
import {
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
  faSave,
  faTrash,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";

export const Editor = function <TDeployment>(props: {
  domain: Domain;
  records: DNSRecord[];
  readonlyMode: boolean;
  saveCall?: (
    domainId: string,
    added: DNSRecord[],
    updated: DNSRecord[],
    removed: DNSRecord[],
  ) => Promise<APIResponse<TDeployment>>;
  onSave: (deployment: TDeployment) => void;
}) {
  const [records, setRecords] = useState<DNSRecord[]>([]);
  useEffect(() => {
    setRecords(props.records);
  }, [props.records]);

  const addedRecords = useMemo(() => records.filter((r) => r.id.startsWith("_new_")), [records]);
  const removedRecords = useMemo(
    () => props.records.filter((r) => !records.find((rr) => rr.id === r.id)),
    [records, props.records],
  );
  const updatedRecords = useMemo(
    () =>
      records.filter(
        (r) =>
          JSON.stringify(props.records.find((rr) => rr.id === r.id)) !== JSON.stringify(r) &&
          !addedRecords.find((ar) => ar.id === r.id),
      ),
    [records, props.records, addedRecords],
  );

  const [errors, setErrors] = useState<string[]>([]);

  const hints: string[] = useMemo(() => {
    const hints: string[] = [];
    if (records.find((r) => r.type === "CNAME" && r.name === props.domain.displayName)) {
      hints.push("You have a CNAME record for the root domain, this is not allowed.");
    }
    if (records.find((r) => r.type === "REDIRECT" && !r.content.startsWith("https://"))) {
      hints.push("It is recommended you only redirect to HTTPS URLs.");
    }
    return hints;
  }, [records, props.domain]);

  const addRecord = () => {
    setRecords([
      ...records,
      { type: "A", name: props.domain.displayName, content: "", ttl: 1, id: `_new_${uuid()}`, proxied: false },
    ]);
  };
  const updateRecord = (id: string, newRecord: Partial<DNSRecord>) => {
    console.log("new dns record", newRecord);
    setRecords(
      // @ts-expect-error newRecord fields are a partial merge, not guaranteed to match DNSRecord's discriminated union
      records.map((r) => {
        if (r.id !== id) {
          return r;
        }
        if (newRecord.type !== undefined && r.type !== newRecord.type) {
          const nr = { ...defaultRecordFields(newRecord.type ?? r.type), ...r, ...newRecord };
          return nr;
        }
        return { ...r, ...newRecord };
      }),
    );
  };
  const removeRecord = (id: string) => {
    setRecords(records.filter((r) => r.id !== id));
  };

  const undoAllChanges = () => {
    setRecords(props.records);
  };

  const unsavedText = useMemo(() => {
    if (addedRecords.length > 0 || removedRecords.length > 0 || updatedRecords.length > 0) {
      return (
        "Unsaved changes: " +
        [
          addedRecords.length > 0 ? `${addedRecords.length} added` : null,
          removedRecords.length > 0 ? `${removedRecords.length} removed` : null,
          updatedRecords.length > 0 ? `${updatedRecords.length} updated` : null,
        ]
          .filter((x) => x !== null)
          .join(", ")
      );
    } else {
      return "";
    }
  }, [addedRecords, updatedRecords, removedRecords]);

  const saveChanges = async () => {
    if (!props.saveCall) {
      return;
    }
    const response = await props.saveCall(props.domain.id, addedRecords, updatedRecords, removedRecords);
    if (response.success) {
      setErrors([]);
      props.onSave(response.data);
    } else {
      console.log(response);
      setErrors(response.errors.length > 0 ? response.errors.flat() : [response.error ?? "Unknown error"]);
    }
  };

  return (
    <div className={"bg-white border-2 border-slate-100 rounded-lg"}>
      {!props.readonlyMode && (
        <div className={"border-b border-slate-200 p-2"}>
          <div className={"flex items-center justify-end space-x-2"}>
            <span className={"text-slate-500 text-sm"}>{unsavedText}</span>
            {unsavedText.length > 0 && (
              <Button
                type={"danger"}
                icon={faUndo}
                size={"sm"}
                text={"Undo changes"}
                onClick={() => undoAllChanges()}
              />
            )}
            <Button
              type={"primary"}
              disabled={unsavedText.length === 0}
              size={"sm"}
              icon={faSave}
              text={"Save changes"}
              onClick={saveChanges}
            />
          </div>
        </div>
      )}
      {errors.length + hints.length > 0 && (
        <div className={"border-b border-slate-200 p-2 flex flex-col space-y-1"}>
          {hints.map((hint, k) => (
            <div key={k} className={"text-blue-700 text-sm flex p-1 items-center"}>
              <FontAwesomeIcon icon={faInfoCircle} className={"text-blue-500 mr-2"} />
              <div>{hint}</div>
            </div>
          ))}
          {errors.map((error, k) => (
            <div key={k} className={"text-red-700 text-sm flex p-1 items-center"}>
              <FontAwesomeIcon icon={faExclamationTriangle} className={"text-red-500 mr-2"} />
              <div>{error}</div>
            </div>
          ))}
          {errors.length > 0 && (
            <div className={"text-red-700 text-sm p-1"}>Please fix the errors above before saving.</div>
          )}
        </div>
      )}

      {records.length > 0 ? (
        <table className={"w-full"}>
          <tbody>
            {records.map(function (record, k) {
              return (
                <tr key={k} className={""}>
                  <td className={`w-32 relative ${k > 0 && "border-t border-slate-100"}`}>
                    <RecordTypeSelect
                      type={record.type}
                      readonlyMode={props.readonlyMode}
                      allowRedirect={records.filter((r) => r.type === "REDIRECT").length < 3}
                      onTypeChange={(type) => updateRecord(record.id, { type: type })}
                    />
                  </td>
                  <td className={`h-10 text-sm px-2 border-l py-1 ${k > 0 && "border-t"} border-slate-100`}>
                    <RecordNameInput
                      readonlyMode={props.readonlyMode}
                      value={record}
                      onChange={(content) => updateRecord(record.id, { ...content })}
                      domain={props.domain.displayName}
                    />
                  </td>
                  <td className={`h-10 text-sm px-2 border-l py-1 w-2/3 ${k > 0 && "border-t"} border-slate-100`}>
                    <RecordContentInputs
                      readonlyMode={props.readonlyMode}
                      domain={props.domain.displayName}
                      value={record}
                      onChange={(content) => updateRecord(record.id, { ...content })}
                    />
                  </td>
                  {!props.readonlyMode && (
                    <td className={`h-10 border-l px-2 ${k > 0 && "border-t"} border-slate-100`}>
                      <IconButton type={"danger"} size={"xs"} icon={faTrash} onClick={() => removeRecord(record.id)} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className={"text-center p-8 font-medium text-slate-500"}>
          You do not have any records yet, add one by clicking the button below.
        </div>
      )}
      {!props.readonlyMode && (
        <button
          className={
            "flex items-center border-t hover:bg-slate-100 rounded-b w-full h-10 py-2 px-3 text-slate-600 hover:text-slate-800"
          }
          onClick={() => addRecord()}
        >
          <FontAwesomeIcon icon={faPlus} className={"h-3 w-3 p-1 "} />
          <div className={"mx-2 text-sm font-medium"}>Add record</div>
        </button>
      )}
    </div>
  );
};

function defaultRecordFields(type: DNSRecordType): Partial<DNSRecord> {
  switch (type) {
    case "A":
    case "AAAA":
    case "CNAME":
      return {
        type: type,
        content: "",
        proxied: false,
      };
    case "MX":
    case "REDIRECT":
      return {
        type: type,
        content: "",
        priority: getDefaultPrioForRecordType(type),
      };
    case "SRV":
      return {
        type: type,
        service: "",
        proto: "",
        target: "",
        priority: 0,
        weight: 0,
        port: 0,
      };
    case "CAA":
      return {
        type: type,
        flags: 0,
        tag: "issue",
        value: "",
      };
    case "TLSA":
      return {
        type: type,
        usage: 3,
        selector: 1,
        matchingType: 1,
        certificate: "",
      };
    case "HTTPS":
      return {
        type: type,
        priority: 1,
        target: ".",
        value: "",
      };
    default:
      return {
        type: type,
        content: "",
      };
  }
}

function getDefaultPrioForRecordType(type: DNSRecordType): number {
  switch (type) {
    case "MX":
      return 10;
    case "REDIRECT":
      return 301;
    default:
      return 0;
  }
}
